import { Box, Stack, Typography, Icon } from '@ace-clublabs/shared-component-library';
import { MembershipOverviewRow } from './MembershipOverviewRow';
import { MembershipOverviewItem } from './MembershipOverviewItem';
import { NewJoinConfirmationData } from '@lib/types';
import { MembershipProductType } from '@ace-clublabs/common-membership-api-types';

interface IMembershipOverviewSection {
  confirmationInfo?: NewJoinConfirmationData;
  numOfAssociates?: number;
  optionsAdded?: number | false | undefined;
  isEnrolledInAutoPay?: boolean;
}

interface IHouseholdMembersComponent {
  confirmationInfo?: NewJoinConfirmationData;
}

interface IOptionsComponent {
  confirmationInfo?: NewJoinConfirmationData;
}

export const MembershipOverviewSection = ({
  confirmationInfo,
  numOfAssociates,
  optionsAdded,
  isEnrolledInAutoPay,
}: IMembershipOverviewSection) => {
  return (
    <div className="membership-overview-container join-flow-overview-container">
      <Box className="membership-overview-header">
        <Typography variant="h6Medium">Membership overview</Typography>
      </Box>
      <div className="membership-overview-info-container">
        <hr className="membership-overview-line"></hr>
        {/** this is a sub section row */}
        <Stack className="membership-overview-row gap-1" flexDirection="row" justifyContent="space-between">
          <Stack className="membership-overview-item" direction="column">
            <Typography className="primary-member-label" variant="b1Medium">
              Primary member
            </Typography>
            <Typography variant="b2Regular" data-di-mask="true" className="qm-mask">
              {confirmationInfo?.firstName} {confirmationInfo?.lastName}
            </Typography>
          </Stack>
          <Stack className="membership-overview-item" direction="column">
            <Typography className="effective-date-label membership-overview-right-align" variant="b1Medium">
              Effective date
            </Typography>
            <Typography variant="b2Regular" className="membership-overview-right-align">
              {confirmationInfo?.effectiveDate}
            </Typography>
          </Stack>
        </Stack>
        {/** this is a sub section row */}
        <Stack className="membership-overview-row gap-1" direction="row" justifyContent="space-between">
          <Stack className="membership-overview-item" direction="column">
            <Typography className="membership-number-label" variant="b1Medium">
              Membership number
            </Typography>
            <Typography variant="b2Regular" data-di-mask="true" className="qm-mask">
              {confirmationInfo?.membershipNumber}
            </Typography>
          </Stack>
          <Stack className="membership-overview-item">
            <Typography className="expiration-date-label membership-overview-right-align" variant="b1Medium">
              Expiration date
            </Typography>
            <Typography variant="b2Regular" className="membership-overview-right-align">
              {confirmationInfo?.expirationDate}
            </Typography>
          </Stack>
        </Stack>
        {!numOfAssociates && !optionsAdded ? (
          <></>
        ) : (
          <MembershipOverviewRow
            leftItem={
              <MembershipOverviewItem heading={'Membership details'} label="membership-details-label">
                {numOfAssociates ? <HouseholdMembersComponent confirmationInfo={confirmationInfo} /> : <></>}
                {numOfAssociates && optionsAdded ? <br /> : <></>}
                {optionsAdded ? <OptionsComponent confirmationInfo={confirmationInfo} /> : <></>}
              </MembershipOverviewItem>
            }
          />
        )}

        <hr className="membership-overview-line"></hr>

        <div className="membership-overview-info-container"></div>
        <Typography variant="h6Medium" className="payment-method-label" sx={{ marginTop: '1rem' }}>
          Payment method
        </Typography>
        <Stack className="membership-overview-row gap-1" direction="row" justifyContent="space-between">
          <Stack className="membership-overview-item">
            <Typography className="payment-type-label" variant="b1Medium">
              Payment type
            </Typography>
            {!['Google Pay', 'Apple Pay'].includes(confirmationInfo?.paymentDetails?.paymentType as string) &&
              <Typography variant="b2Regular" className="credit-or-debit-label">
                {confirmationInfo?.paymentDetails?.paymentType}
              </Typography>
            }{confirmationInfo?.paymentDetails?.paymentType === 'Google Pay' &&
              <Icon
                iconName={'google-pay-logo'}
                sx={{ fontSize: '4.75rem', marginLeft: '-0.75rem', marginTop: '-0.75rem', marginBottom: '-0.75rem' }}
              ></Icon>
            }{confirmationInfo?.paymentDetails?.paymentType === 'Apple Pay' &&
              <Icon
                iconName={'apple-pay-logo'}
                sx={{ fontSize: '2.75rem' }}
              ></Icon>
            }
          </Stack>
          <Stack className="membership-overview-item">
            <Typography className="membership-overview-right-align card-type-label" variant="b1Medium">
              Card type
            </Typography>
            <Typography variant="b2Regular" className="membership-overview-right-align">
              {confirmationInfo?.paymentDetails?.card?.brand}
            </Typography>
          </Stack>
        </Stack>

        <Stack className="membership-overview-row gap-1" direction="row" justifyContent="space-between">
          <Stack className="membership-overview-item">
            <Typography className="last-four-label" variant="b1Medium">
              Last 4 digits on card
            </Typography>
            <Typography variant="b2Regular" data-di-mask="true" className="qm-mask">
              {confirmationInfo?.paymentDetails?.card?.last4}
            </Typography>
          </Stack>

          <Stack className="membership-overview-item">
            <Typography className="membership-overview-right-align card-expiry-date-label" variant="b1Medium">
              Card exp date
            </Typography>
            <Typography variant="b2Regular" className="membership-overview-right-align">
              {confirmationInfo?.paymentDetails?.card?.expDate}
            </Typography>
          </Stack>
        </Stack>

        <Stack className="membership-overview-row gap-1" direction="row" justifyContent="space-between">
          <Stack className="membership-overview-item">
            <Typography className="total-paid-label" variant="b1Medium">
              Total paid
            </Typography>
            <Typography variant="b2Regular">${confirmationInfo?.amount}</Typography>
          </Stack>
        </Stack>

        <Stack className="membership-overview-row gap-1" direction="row" justifyContent="space-between">
          <Stack className="membership-overview-item">
            <Typography className="auto-pay-label" variant="b1Medium">
              Auto pay
            </Typography>
            <Typography variant="b2Regular">{isEnrolledInAutoPay ? <>Enrolled</> : <>Not Enrolled</>}</Typography>
          </Stack>
        </Stack>
      </div>
    </div>
  );
};

const HouseholdMembersComponent = ({ confirmationInfo }: IHouseholdMembersComponent): JSX.Element => {
  return (
    <>
      <Typography variant="b2Regular" className="household-members-label">
        Household members:
      </Typography>
      {confirmationInfo?.members.map((member) => {
        const { firstName, lastName } = member;
        const fullName = firstName + ' ' + lastName;
        return (
          <Typography
            variant="b2Regular"
            key={fullName}
            data-di-mask="true"
            className={`member-${firstName}-${lastName} membership-overview-content qm-mask`}
          >
            {`${fullName}`}
          </Typography>
        );
      })}
    </>
  );
};

const OptionsComponent = ({ confirmationInfo }: IOptionsComponent): JSX.Element => {
  const optionsDescriptions = {
    [MembershipProductType.RV]: 'RV/motorcycle roadside assistance',
    [MembershipProductType.ERS]: 'Immediate Roadside (one-time)',
  };
  return (
    <>
      <Typography variant="b1Medium" className="options-label">
        Options:
      </Typography>
      {confirmationInfo?.options.map((productType) => {
        return (
          <Typography
            variant="b2Regular"
            key={productType}
            data-di-mask="true"
            className={`option-${productType} qm-mask`}
          >
            {optionsDescriptions[productType]}
          </Typography>
        );
      })}
    </>
  );
};
